import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useBulletins } from 'hooks/useBulletins';
import { useAddBulletin } from 'hooks/useAddBulletin';
import { useCategories } from 'hooks/useCategories';
import type { Bulletin } from 'api/v1/requests/bulletins';
import {
  formatBulletinGrades,
  formatBulletinPreviewLines,
  formatBulletinSubjectTitle,
  formatSubjectSlug,
} from 'utils/bulletinLabels';
import { TaskWizardModal } from 'modules/Details/NewTask/TaskWizardModal';
import { useTaskFormStore } from 'modules/Details/NewTask/taskForm.store';
import {
  CircleCheck,
  CirclePause,
  CircleX,
  ClipboardList,
  Clock3,
  Lock,
  Pencil,
  Plus,
} from 'lucide-react';
import { CabinetButton, EmptyState, RequestCard } from 'ui/kit/Kit';
import { GoalIllustration } from 'ui/GoalIllustration/GoalIllustration';
import { StatusMark, type StatusTone } from 'ui/kit/StatusMark';
import kit from 'ui/kit/kit.module.scss';
import { PageHead } from 'ui/kit/PageHead';
import { FadeIn, StepBody, staggerDelay } from 'ui/kit/uiMotion';
import { addNotification } from 'notifications';
import { METRIKA_GOALS, reachMetrikaGoal } from 'utils/metrikaGoals';
import {
  clearPodborDraft,
  consumeServerPodborDraft,
  draftToTaskFormValues,
  fetchServerPodborDraft,
  mapPodborDraftToBulletin,
  readPodborDraft,
} from 'utils/podborDraft';
import { useGetCurrentProfile } from '../model/services';

import screen from 'modules/Details/StudentTaskScreen.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

type TasksTab = 'active' | 'archive';

const isActiveTask = (task: Bulletin): boolean => Boolean(task.active && !task.closed);
const isArchiveTask = (task: Bulletin): boolean => Boolean(!task.active || task.closed);

const taskStatusMark = (
  task: Bulletin,
): { label: string; word: string; tone: StatusTone; icon: typeof Lock } => {
  if (task.closed) return { label: 'Заявка закрыта', word: 'Закрыта', tone: 'slate', icon: Lock };
  if (task.verification_status === 'pending_review') {
    return { label: 'На модерации', word: 'Модерация', tone: 'amber', icon: Clock3 };
  }
  if (task.verification_status === 'rejected') {
    return { label: 'Отклонена модератором', word: 'Отклонена', tone: 'rose', icon: CircleX };
  }
  if (task.verification_status === 'changes_requested') {
    return { label: 'Нужны правки модератора', word: 'Правки', tone: 'orange', icon: Pencil };
  }
  if (task.active) {
    return { label: 'Отклики на задачу включены', word: 'Включены', tone: 'green', icon: CircleCheck };
  }
  return { label: 'Отклики на задачу отключены', word: 'Выключены', tone: 'slate', icon: CirclePause };
};

export const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<TasksTab>('active');

  const { data: categories } = useCategories();
  const { data: profile } = useGetCurrentProfile();
  const addBulletin = useAddBulletin();
  const { setValues } = useTaskFormStore();
  const hireIntentApplied = useRef(false);
  const podborApplied = useRef(false);
  const podborStarted = useRef(false);
  const [podborReady, setPodborReady] = useState(false);

  // One fetch, client-split: Active = active && !closed; Archive = !active || closed.
  const { data: allBulletins, isLoading } = useBulletins({
    search: '',
    closed: null,
    active: null,
  });

  const bulletins = useMemo(() => {
    if (!allBulletins) return [];
    return allBulletins.filter((task) =>
      tab === 'active' ? isActiveTask(task) : isArchiveTask(task),
    );
  }, [allBulletins, tab]);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!podborReady) return;
    if (podborApplied.current) return;
    if (hireIntentApplied.current) return;
    if (readPodborDraft()) return;
    if (searchParams.get('create') !== '1') return;

    const cat = searchParams.get('cat')?.trim() || '';
    const goal = searchParams.get('goal')?.trim() || '';
    const tutor = Number(searchParams.get('tutor') || 0);
    const hasHirePrefill = Boolean(cat || goal || (Number.isInteger(tutor) && tutor > 0));
    if (!hasHirePrefill && (allBulletins || []).length > 0) return;
    const current = useTaskFormStore.getState().values;
    const next = { ...current };
    const slugOk = (value: string) => /^[\w-]+$/.test(value);

    if (cat && slugOk(cat)) {
      next.subject = cat;
      const found = categories?.find((item) => item.name === cat);
      next.subjectLabel =
        (found as { title?: string } | undefined)?.title ||
        formatSubjectSlug(cat) ||
        cat;
      if (goal && slugOk(goal)) {
        next.goal = goal;
        const sub = found?.subcategories?.find((item) => item.name === goal);
        next.goalLabel =
          (sub as { title?: string } | undefined)?.title || formatSubjectSlug(goal) || goal;
      }
    } else if (goal && slugOk(goal)) {
      next.goal = goal;
    }
    if (Number.isInteger(tutor) && tutor > 0) {
      next.preferredTutorProfileId = tutor;
    }
    if (!hireIntentApplied.current) {
      setValues(next);
      hireIntentApplied.current = true;
    }
    setCurrentIndex(0);
    setModalOpen(true);
  }, [searchParams, setValues, categories, podborReady, allBulletins]);

  useEffect(() => {
    if (podborStarted.current) {
      if (podborApplied.current || podborReady) setPodborReady(true);
      return;
    }
    if (!profile) return;
    if (isLoading) return;
    podborStarted.current = true;

    void (async () => {
      let handled = false;
      try {
        const peek = await fetchServerPodborDraft();
        const hasLocalBulletins = (allBulletins || []).length > 0;
        if (peek.exists && peek.consumed && hasLocalBulletins) {
          clearPodborDraft();
          return;
        }
        if (peek.exists && (!peek.consumed || !hasLocalBulletins)) {
          const result = await consumeServerPodborDraft();
          if (result.status === 'created') {
            podborApplied.current = true;
            handled = true;
            reachMetrikaGoal(METRIKA_GOALS.taskCreated);
            clearPodborDraft();
            addNotification('Заявка создана по вашему описанию', 'success');
            const id = result.bulletin_id;
            if (id) {
              navigate(`/profile/details/task/${id}`, { replace: true });
            }
          }
        }
      } catch {
        /* cookie fallback */
      }

      if (handled) return;

      const draft = readPodborDraft();
      if (!draft) return;

      podborApplied.current = true;
      const registrantType =
        draft.registrantType ||
        ((profile as { registrant_type?: string }).registrant_type as 'self' | 'parent' | undefined) ||
        'self';

      if ((allBulletins || []).length > 0) {
        clearPodborDraft();
        return;
      }

      try {
        const payload = mapPodborDraftToBulletin(draft, {
          registrant_type: registrantType,
          name: (profile as { name?: string }).name,
          surname: (profile as { surname?: string }).surname,
        });
        const result = await addBulletin.mutateAsync(payload);
        reachMetrikaGoal(METRIKA_GOALS.taskCreated);
        clearPodborDraft();
        addNotification('Заявка создана по вашему описанию', 'success');
        const id = (result as { id?: number })?.id;
        if (id) {
          navigate(`/profile/details/task/${id}`, { replace: true });
        } else {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('create');
          setSearchParams(nextParams, { replace: true });
        }
      } catch {
        setValues({
          ...useTaskFormStore.getState().values,
          ...draftToTaskFormValues(draft),
        });
        addNotification('Не удалось создать заявку автоматически - проверьте шаги мастера', 'info');
        setCurrentIndex(0);
        setModalOpen(true);
      }
    })().finally(() => setPodborReady(true));
  }, [profile, addBulletin, navigate, searchParams, setSearchParams, setValues, isLoading, allBulletins]);

  useEffect(() => {
    if (!categories?.length) return;
    const current = useTaskFormStore.getState().values;
    const cat = String(current.subject || '');
    const goal = String(current.goal || '');
    const found = categories.find((item) => item.name === cat);
    if (!found) return;
    const patch: Record<string, unknown> = {};
    const categoryTitle = (found as { title?: string }).title;
    if (categoryTitle && current.subjectLabel !== categoryTitle) {
      patch.subjectLabel = categoryTitle;
    }
    const sub = found.subcategories?.find((item) => item.name === goal);
    const goalTitle = (sub as { title?: string } | undefined)?.title;
    if (goalTitle && current.goalLabel !== goalTitle) {
      patch.goalLabel = goalTitle;
    }
    if (Object.keys(patch).length) {
      setValues({ ...current, ...patch });
    }
  }, [categories, setValues]);

  const openModal = (index: number) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    if (searchParams.get('create') !== '1') return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    nextParams.delete('cat');
    nextParams.delete('goal');
    nextParams.delete('tutor');
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <main className={screen.page} style={{ position: 'relative' }}>
      {/* pagehead-2026-10-09: общая шапка кабинета */}
      <PageHead crumb='Личный кабинет / Мои задачи' title='Мои задачи'>
          <div className={kit.listHeadTabs} role='tablist' aria-label='Список задач'>
            <button
              type='button'
              role='tab'
              aria-selected={tab === 'active'}
              className={tab === 'active' ? kit.listHeadTabOn : kit.listHeadTab}
              onClick={() => setTab('active')}
            >
              Активные
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={tab === 'archive'}
              className={tab === 'archive' ? kit.listHeadTabOn : kit.listHeadTab}
              onClick={() => setTab('archive')}
            >
              Архив
            </button>
          </div>
          {/* r2: кнопка всегда в шапке (и в «Активных», и в «Архиве»), чтобы шапка не прыгала при переключении вкладок. */}
          <CabinetButton
            className={kit.listHeadAction}
            onClick={() => openModal(0)}
            aria-label='Создать задачу'
            disabled={isLoading}
          >
            <Plus size={18} strokeWidth={2.4} aria-hidden />
            <span className={kit.listHeadActionLong}>Создать задачу</span>
            <span className={kit.listHeadActionShort}>Создать</span>
          </CabinetButton>
        </PageHead>

      <StepBody stepKey={tab}>
      <div className={screen.list}>
        {isLoading && <BrandLoader center label='Загрузка...' />}

        {!isLoading && bulletins.length === 0 && tab === 'active' && (
          <EmptyState
            title='Пока нет задач'
            fact='Создай задачу по математике. Укажи цель, класс и бюджет - подходящие репетиторы смогут откликнуться.'
            hint='После создания здесь появятся задача и отклики.'
          >
            <span className={kit.emptyArt} aria-hidden>
              <ClipboardList size={48} strokeWidth={1.5} />
            </span>
          </EmptyState>
        )}

        {!isLoading && bulletins.length === 0 && tab === 'archive' && (
          <EmptyState
            title='В архиве пока пусто'
            fact='Здесь будут задачи, которые ты закроешь.'
            action={<CabinetButton onClick={() => setTab('active')}>Смотреть активные</CabinetButton>}
          />
        )}

        {bulletins.map((task, index) => {
          const grade = formatBulletinGrades(task);
          const wish = task.wishes?.trim() || '';
          const meta = formatBulletinPreviewLines(task)
            .filter((line) => line !== grade && !line.startsWith('Пожелания:'))
            .slice(0, 3);
          const status = taskStatusMark(task);
          return (
            <FadeIn key={task.id} delay={staggerDelay(index)}>
              <div className={screen.taskCard}>
                <RequestCard
                  eyebrow={<StatusMark label={status.label} word={status.word} tone={status.tone} icon={status.icon} />}
                  title={formatBulletinSubjectTitle(task.subject_category)}
                  subtitle={grade || undefined}
                  meta={meta}
                  note={wish || undefined}
                  art={<GoalIllustration subject={task.subject_category} fit='cover' />}
                  action={
                    <CabinetButton onClick={() => navigate(`/profile/details/task/${task.id}`)}>
                      Открыть задачу
                    </CabinetButton>
                  }
                />
              </div>
            </FadeIn>
          );
        })}
      </div>
      </StepBody>

      {modalOpen && (
        <TaskWizardModal
          open={modalOpen}
          onClose={closeModal}
          initialStep={currentIndex}
        />
      )}
    </main>
  );
};
